import { Text } from "@sfc/ui";
import {
  ALL_STATUSES,
  type SkeletonStatusCounts,
} from "@client/hooks/use-my-evaluations-data";
import type { EvaluationState } from "@engine/round/aggregate";
import { cn } from "@client/utils/cn";

interface StatusCardProps {
  count: number | null;
  label: string;
  color: "success" | "foreground";
  cardClassName?: string;
  isSelected: boolean;
  onClick: () => void;
}

function StatusCard({
  count,
  label,
  color,
  cardClassName,
  isSelected,
  onClick,
}: StatusCardProps) {
  const colorClasses: Record<typeof color, string> = {
    success: "text-success",
    foreground: "text-foreground",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-xl border bg-surface-base px-4 py-3 text-left transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-accent focus-visible:ring-offset-2",
        isSelected
          ? "border-2 border-accent"
          : "border-2 border-border-subtle hover:bg-surface-hover",
        cardClassName,
      )}
    >
      <div className="flex flex-col items-center justify-center gap-1">
        <span
          className={`text-heading-lg font-semibold ${colorClasses[color]}`}
        >
          {count ?? "—"}
        </span>
        <Text as="span" variant="body-sm" tone="muted-foreground">
          {label}
        </Text>
      </div>
    </button>
  );
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
}

export function ProposalsStatusSummaryCards({
  counts,
  statusFilter,
  showHidden,
  onStatusFilterChange,
  onShowHiddenChange,
}: {
  counts: SkeletonStatusCounts;
  statusFilter: EvaluationState[];
  showHidden: boolean;
  onStatusFilterChange: (filter: EvaluationState[]) => void;
  onShowHiddenChange: (show: boolean) => void;
}) {
  // Model B: "no filter" = statusFilter === []. Clicking a status card sets
  // the filter to just that one; clicking it again (or clicking Total) clears.
  const handleStatusClick = (status: EvaluationState) => {
    onShowHiddenChange(false);

    if (!showHidden && arraysEqual(statusFilter, [status])) {
      onStatusFilterChange([]);
    } else {
      onStatusFilterChange([status]);
    }
  };

  const handleTotalClick = () => {
    onShowHiddenChange(false);
    onStatusFilterChange([]);
  };

  const isOnlyStatus = (status: EvaluationState) =>
    !showHidden && arraysEqual(statusFilter, [status]);

  // Total is "selected" whenever the status filter isn't narrowing — either
  // empty or fully selected both mean "show everything."
  const isTotalSelected =
    !showHidden &&
    (statusFilter.length === 0 || statusFilter.length === ALL_STATUSES.length);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <StatusCard
        count={counts.evaluated}
        label="Evaluation Started"
        color="success"
        isSelected={isOnlyStatus("evaluated")}
        onClick={() => handleStatusClick("evaluated")}
      />
      <StatusCard
        count={counts.notStarted}
        label="Not Started"
        color="foreground"
        isSelected={isOnlyStatus("not-started")}
        onClick={() => handleStatusClick("not-started")}
      />
      <StatusCard
        count={counts.deferred}
        label="Deferred"
        color="foreground"
        isSelected={isOnlyStatus("deferred")}
        onClick={() => handleStatusClick("deferred")}
      />
      <StatusCard
        count={counts.recused}
        label="Recused"
        color="foreground"
        isSelected={isOnlyStatus("recused")}
        onClick={() => handleStatusClick("recused")}
      />
      <StatusCard
        count={counts.canceled}
        label="Canceled"
        color="foreground"
        isSelected={isOnlyStatus("canceled")}
        onClick={() => handleStatusClick("canceled")}
      />
      <StatusCard
        count={counts.total}
        label="Total"
        color="foreground"
        isSelected={isTotalSelected}
        onClick={handleTotalClick}
      />
    </div>
  );
}
